// Phase SIKA-4b — Cross-product win banking hook.
//
// Calls public.sika_bank_external_win(goal_id, source, source_id, amount).
// Auth-only. Idempotent server-side — repeat calls return already_banked.

import { useCallback, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export type SikaBankingSource =
  | "flex"
  | "singles_chain"
  | "super_acca"
  | "powerball"
  | "owl_hour"
  | "daily_acca";

export interface BankExternalWinInput {
  goal_id: string;
  source: SikaBankingSource;
  source_id: string;
  amount: number;
}

export interface BankExternalWinResult {
  ok: boolean;
  already_banked?: boolean;
  banking_id?: string;
  goal_id?: string;
  amount?: number;
  new_current_amount?: number;
  new_banked_amount?: number;
}

export interface UseBankExternalWinState {
  bankWin: (input: BankExternalWinInput) => Promise<BankExternalWinResult>;
  loading: boolean;
  error: string | null;
}

export function useBankExternalWin(): UseBankExternalWinState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bankWin = useCallback<UseBankExternalWinState["bankWin"]>(
    async (input) => {
      if (!user) {
        const msg = "useBankExternalWin: unauthenticated";
        setError(msg);
        return { ok: false } as BankExternalWinResult;
      }
      setLoading(true);
      setError(null);
      const rpc = supabase.rpc as unknown as (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: { message: string } | null }>;
      const { data, error: rpcErr } = await rpc("sika_bank_external_win", {
        p_goal_id: input.goal_id,
        p_source: input.source,
        p_source_id: input.source_id,
        p_amount: input.amount,
      });
      setLoading(false);
      if (rpcErr) {
        setError(rpcErr.message);
        return { ok: false } as BankExternalWinResult;
      }
      return (data as BankExternalWinResult) ?? { ok: false };
    },
    [supabase, user],
  );

  return { bankWin, loading, error };
}
